<template>
  <div class="home-settings">
    <page-header title="تنظیمات صفحه اصلی" english-toggle></page-header>

    <v-row v-if="loaded">
      <v-col cols="12" lg="3">
        <v-card class="settings-card settings-nav" color="#393E46">
          <v-list dense color="transparent">
            <v-subheader>بخش‌ها</v-subheader>
            <v-list-item v-for="section in navSections" :key="section.id" @click="scrollTo(section.id)">
              <v-list-item-icon class="ml-3">
                <v-icon color="#00ADB5">{{ section.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ section.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
          <div class="pa-4">
            <v-btn block large depressed class="btn-primary" @click="updateContent">
              <v-icon left>mdi-content-save</v-icon>
              ذخیره تغییرات
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" lg="9">
        <!-- Section visibility -->
        <v-card id="section-visibility" class="settings-card" color="#393E46">
          <v-card-title><v-icon left color="#00ADB5">mdi-eye</v-icon>نمایش بخش‌ها</v-card-title>
          <v-card-subtitle>بخش‌هایی که در صفحه اصلی سایت نمایش داده می‌شوند.</v-card-subtitle>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="4" v-for="(label, key) in sectionLabels" :key="key">
                <v-switch v-model="sections[key]" :label="label" color="#00ADB5" inset hide-details></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Hero -->
        <v-card id="section-hero" class="settings-card" color="#393E46">
          <v-card-title><v-icon left color="#00ADB5">mdi-image-area</v-icon>بخش اصلی (بالای صفحه)</v-card-title>
          <v-card-text>
            <picture-field name="home_page_picture_1" :current="settings.home_page_picture_1.en_value"
                           hint="پیشنهاد: تصویر افقی با عرض حداقل ۱۹۲۰ پیکسل"
                           @selected="pictureSelected"></picture-field>
            <translated-field v-model="settings.home_page_title_1" label="عنوان"></translated-field>
            <translated-field v-model="settings.home_page_description_1" label="توضیحات" textarea></translated-field>
            <translated-field v-model="settings.home_page_link_title_1" label="متن دکمه"></translated-field>
            <translated-field v-model="settings.home_page_link_1" label="لینک دکمه" :hint="linkHint" ltr></translated-field>
          </v-card-text>
        </v-card>

        <!-- Features -->
        <v-card id="section-features" class="settings-card" color="#393E46">
          <v-card-title><v-icon left color="#00ADB5">mdi-star-circle</v-icon>ویژگی‌ها</v-card-title>
          <v-card-subtitle>کارت‌های کوچک زیر تصویر اصلی (حداکثر {{ maxFeatures }} مورد).</v-card-subtitle>
          <v-card-text>
            <translated-field v-model="settings.home_page_features_title" label="عنوان بخش"></translated-field>

            <div v-for="(feature, index) in features" :key="index" class="list-item-card">
              <div class="list-item-card__header">
                <span class="list-item-card__index">{{ index + 1 }}</span>
                <v-spacer></v-spacer>
                <v-btn icon small :disabled="index === 0" @click="move(features, index, -1)"><v-icon>mdi-arrow-up</v-icon></v-btn>
                <v-btn icon small :disabled="index === features.length - 1" @click="move(features, index, 1)"><v-icon>mdi-arrow-down</v-icon></v-btn>
                <v-btn icon small color="#fb4e4e" @click="features.splice(index, 1)"><v-icon color="#fb4e4e">mdi-delete</v-icon></v-btn>
              </div>
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-select v-model="feature.icon" :items="featureIcons" item-text="label" item-value="value"
                            label="آیکون" outlined dense color="#00ADB5">
                    <template v-slot:selection="{ item }">
                      <v-icon class="ml-2" color="#00ADB5">{{ item.value }}</v-icon>{{ item.label }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-icon class="ml-2" color="#00ADB5">{{ item.value }}</v-icon>{{ item.label }}
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" :md="$store.state.showEnglishFields ? 4 : 8">
                  <v-text-field v-model="feature.fa_title" label="عنوان (فارسی)" outlined dense color="#00ADB5"></v-text-field>
                </v-col>
                <v-col v-if="$store.state.showEnglishFields" cols="12" md="4">
                  <v-text-field v-model="feature.en_title" label="عنوان (English)" outlined dense dir="ltr" color="#00ADB5"></v-text-field>
                </v-col>
                <v-col cols="12" :md="$store.state.showEnglishFields ? 6 : 12">
                  <v-text-field v-model="feature.fa_text" label="توضیح (فارسی)" outlined dense color="#00ADB5"></v-text-field>
                </v-col>
                <v-col v-if="$store.state.showEnglishFields" cols="12" md="6">
                  <v-text-field v-model="feature.en_text" label="توضیح (English)" outlined dense dir="ltr" color="#00ADB5"></v-text-field>
                </v-col>
              </v-row>
            </div>

            <v-btn outlined class="btn-secondary" :disabled="features.length >= maxFeatures" @click="addFeature">
              <v-icon left>mdi-plus</v-icon>
              افزودن ویژگی
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Featured products -->
        <v-card id="section-featured" class="settings-card" color="#393E46">
          <v-card-title><v-icon left color="#00ADB5">mdi-toolbox</v-icon>محصولات منتخب</v-card-title>
          <v-card-subtitle>به ترتیب زیر در اسلایدر صفحه اصلی نمایش داده می‌شوند.</v-card-subtitle>
          <v-card-text>
            <translated-field v-model="settings.home_page_featured_title" label="عنوان بخش"></translated-field>
            <translated-field v-model="settings.home_page_featured_subtitle" label="زیرعنوان بخش"></translated-field>

            <v-autocomplete
                v-model="productToAdd"
                :items="selectableProducts"
                :filter="filterProducts"
                item-text="fa_name"
                item-value="id"
                label="جستجو و افزودن محصول (نام یا کد فنی)"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                color="#00ADB5"
                :disabled="featuredIds.length >= maxFeaturedProducts"
                :hint="featuredIds.length + ' از ' + maxFeaturedProducts + ' محصول انتخاب شده'"
                persistent-hint
                @change="addFeaturedProduct"
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar tile>
                  <img v-if="item.image_url" :src="item.image_url" alt="" class="product-thumb">
                  <v-icon v-else>mdi-image-off</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.fa_name }}</v-list-item-title>
                  <v-list-item-subtitle dir="ltr" class="text-right">{{ item.article_number }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <div v-if="!featuredIds.length" class="empty-state">
              <v-icon large color="#888">mdi-package-variant</v-icon>
              <div>هنوز محصولی انتخاب نشده است. اسلایدر محصولات در سایت نمایش داده نمی‌شود.</div>
            </div>

            <v-list v-else color="transparent" class="mt-4">
              <v-list-item v-for="(id, index) in featuredIds" :key="id" class="featured-product">
                <span class="list-item-card__index ml-3">{{ index + 1 }}</span>
                <v-list-item-avatar tile>
                  <img v-if="productById[id] && productById[id].image_url" :src="productById[id].image_url" alt="" class="product-thumb">
                  <v-icon v-else>mdi-image-off</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ productById[id] ? productById[id].fa_name : ('محصول #' + id) }}</v-list-item-title>
                  <v-list-item-subtitle v-if="productById[id]" dir="ltr" class="text-right">{{ productById[id].article_number }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-chip v-if="productById[id] && !productById[id].status" small color="#fb4e4e" class="ml-2">
                  غیرفعال - در سایت نمایش داده نمی‌شود
                </v-chip>
                <v-list-item-action class="flex-row">
                  <v-btn icon small :disabled="index === 0" @click="move(featuredIds, index, -1)"><v-icon>mdi-arrow-up</v-icon></v-btn>
                  <v-btn icon small :disabled="index === featuredIds.length - 1" @click="move(featuredIds, index, 1)"><v-icon>mdi-arrow-down</v-icon></v-btn>
                  <v-btn icon small @click="featuredIds.splice(index, 1)"><v-icon color="#fb4e4e">mdi-close</v-icon></v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- Categories & blog -->
        <v-card id="section-lists" class="settings-card" color="#393E46">
          <v-card-title><v-icon left color="#00ADB5">mdi-shape</v-icon>دسته‌بندی‌ها و مطالب</v-card-title>
          <v-card-subtitle>این بخش‌ها به صورت خودکار از دسته‌بندی‌های اصلی و آخرین مطالب بلاگ پر می‌شوند.</v-card-subtitle>
          <v-card-text>
            <translated-field v-model="settings.home_page_categories_title" label="عنوان بخش دسته‌بندی‌ها"></translated-field>
            <translated-field v-model="settings.home_page_blog_title" label="عنوان بخش مطالب"></translated-field>
          </v-card-text>
        </v-card>

        <!-- About -->
        <v-card id="section-about" class="settings-card" color="#393E46">
          <v-card-title><v-icon left color="#00ADB5">mdi-information</v-icon>درباره ما</v-card-title>
          <v-card-text>
            <picture-field name="home_page_picture_2" :current="settings.home_page_picture_2.en_value"
                           hint="پیشنهاد: تصویر با نسبت ۳ به ۲"
                           @selected="pictureSelected"></picture-field>
            <translated-field v-model="settings.home_page_title_2" label="عنوان"></translated-field>
            <translated-field v-model="settings.home_page_description_2" label="توضیحات" textarea></translated-field>
            <translated-field v-model="settings.home_page_link_title_2" label="متن دکمه"></translated-field>
            <translated-field v-model="settings.home_page_link_2" label="لینک دکمه" :hint="linkHint" ltr></translated-field>
          </v-card-text>
        </v-card>

        <!-- Call to action -->
        <v-card id="section-cta" class="settings-card" color="#393E46">
          <v-card-title><v-icon left color="#00ADB5">mdi-bullhorn</v-icon>دعوت به اقدام (پایین صفحه)</v-card-title>
          <v-card-text>
            <picture-field name="home_page_picture_3" :current="settings.home_page_picture_3.en_value"
                           hint="اختیاری - با شفافیت کم پشت متن نمایش داده می‌شود"
                           removable
                           @selected="pictureSelected"
                           @remove="pictureRemoved"></picture-field>
            <translated-field v-model="settings.home_page_title_3" label="عنوان"></translated-field>
            <translated-field v-model="settings.home_page_description_3" label="توضیحات" textarea></translated-field>
            <translated-field v-model="settings.home_page_link_title_3" label="متن دکمه"></translated-field>
            <translated-field v-model="settings.home_page_link_3" label="لینک دکمه" :hint="linkHint" ltr></translated-field>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import PageHeader from "@/components/General/PageHeader.vue";
import TranslatedField from "@/components/General/TranslatedField.vue";
import PictureField from "@/components/File/PictureField.vue";

// Must stay within FEATURE_ICONS in tamin-sanat-front/scripts/build-mdi-subset.py:
// the site only ships the glyphs listed there.
const HOME_FEATURE_ICONS = [
  {value: 'mdi-factory', label: 'کارخانه'},
  {value: 'mdi-headset', label: 'پشتیبانی / مشاوره'},
  {value: 'mdi-truck-fast', label: 'ارسال سریع'},
  {value: 'mdi-tools', label: 'ابزار / خدمات فنی'},
  {value: 'mdi-shield-check', label: 'ضمانت'},
  {value: 'mdi-certificate', label: 'گواهینامه'},
  {value: 'mdi-cog', label: 'تنظیمات / مهندسی'},
  {value: 'mdi-lightning-bolt', label: 'برق'},
  {value: 'mdi-chip', label: 'الکترونیک'},
  {value: 'mdi-robot-industrial', label: 'ربات صنعتی'},
  {value: 'mdi-cash-multiple', label: 'قیمت مناسب'},
  {value: 'mdi-clock-fast', label: 'سرعت'},
  {value: 'mdi-handshake', label: 'همکاری'},
  {value: 'mdi-package-variant-closed', label: 'بسته / کالا'},
  {value: 'mdi-star-circle', label: 'ستاره'},
  {value: 'mdi-earth', label: 'جهانی'},
  {value: 'mdi-phone-in-talk', label: 'تماس'},
  {value: 'mdi-account-tie', label: 'کارشناس'},
]

const SECTION_LABELS = {
  features: 'ویژگی‌ها',
  featured: 'محصولات منتخب',
  categories: 'دسته‌بندی‌ها',
  about: 'درباره ما',
  blog: 'آخرین مطالب',
  cta: 'دعوت به اقدام',
}

export default {
  name: "HomePageSetting",

  components: {PageHeader, TranslatedField, PictureField},

  data() {
    return {
      loaded: false,
      settings: {},
      features: [],
      featuredIds: [],
      sections: {},
      newPictures: {},
      removedPictures: [],
      products: [],
      productToAdd: null,

      featureIcons: HOME_FEATURE_ICONS,
      sectionLabels: SECTION_LABELS,
      maxFeatures: 8,
      maxFeaturedProducts: 24,
      linkHint: 'آدرس داخلی سایت مثل /categories یا آدرس کامل https://...',
      navSections: [
        {id: 'section-visibility', title: 'نمایش بخش‌ها', icon: 'mdi-eye'},
        {id: 'section-hero', title: 'بخش اصلی', icon: 'mdi-image-area'},
        {id: 'section-features', title: 'ویژگی‌ها', icon: 'mdi-star-circle'},
        {id: 'section-featured', title: 'محصولات منتخب', icon: 'mdi-toolbox'},
        {id: 'section-lists', title: 'دسته‌بندی‌ها و مطالب', icon: 'mdi-shape'},
        {id: 'section-about', title: 'درباره ما', icon: 'mdi-information'},
        {id: 'section-cta', title: 'دعوت به اقدام', icon: 'mdi-bullhorn'},
      ],
    }
  },

  computed: {
    productById() {
      return Object.fromEntries(this.products.map((product) => [product.id, product]))
    },

    selectableProducts() {
      return this.products.filter((product) => !this.featuredIds.includes(product.id))
    },
  },

  mounted() {
    this.getHomePage()
    this.getAllProducts()
  },

  methods: {
    getHomePage() {
      this.$store.dispatch("getHomePageContent").then((res) => {
        const settings = res.data
        this.features = (settings.home_page_features.en_value || []).map((feature) => ({...feature}))
        this.featuredIds = [...(settings.home_page_featured_product.en_value || [])]
        this.sections = Object.fromEntries(
            Object.keys(SECTION_LABELS).map((key) => [key, (settings.home_page_sections.en_value || {})[key] !== false])
        )
        this.settings = settings
        this.newPictures = {}
        this.removedPictures = []
        this.loaded = true
      })
    },

    getAllProducts() {
      this.$store.dispatch("getAllProducts").then((res) => {
        this.products = res.data
      })
    },

    updateContent() {
      this.$store.dispatch("updateHomePage", {
        settings: this.settings,
        pictures: this.newPictures,
        removedPictures: this.removedPictures,
        featuredIds: this.featuredIds,
        features: this.features,
        sections: this.sections,
      }).then(() => {
        this.getHomePage()
      })
    },

    pictureSelected(file, name) {
      this.$set(this.newPictures, name, file)
      this.removedPictures = this.removedPictures.filter((removed) => removed !== name)
    },

    pictureRemoved(name) {
      this.$delete(this.newPictures, name)
      if (!this.removedPictures.includes(name)) {
        this.removedPictures.push(name)
      }
    },

    addFeature() {
      this.features.push({icon: 'mdi-star-circle', fa_title: '', en_title: '', fa_text: '', en_text: ''})
    },

    addFeaturedProduct(id) {
      if (id && !this.featuredIds.includes(id)) {
        this.featuredIds.push(id)
      }
      this.$nextTick(() => {
        this.productToAdd = null
      })
    },

    filterProducts(item, query) {
      const needle = query.toLowerCase()
      return (item.fa_name || '').toLowerCase().includes(needle) ||
          (item.article_number || '').toLowerCase().includes(needle)
    },

    move(list, index, offset) {
      const [item] = list.splice(index, 1)
      list.splice(index + offset, 0, item)
    },

    scrollTo(id) {
      document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'})
    },
  }
}
</script>

<style scoped>
.settings-nav {
  position: sticky;
  top: 80px;
}

.list-item-card {
  border: 1px solid rgba(238, 238, 238, 0.12);
  border-radius: 12px;
  padding: 12px 16px 4px;
  margin-bottom: 16px;
  background: rgba(34, 40, 49, 0.45);
}

.list-item-card__header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.list-item-card__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #00ADB5;
  color: #222831;
  font-size: 13px;
  flex-shrink: 0;
}

.featured-product {
  border: 1px solid rgba(238, 238, 238, 0.12);
  border-radius: 12px;
  margin-bottom: 8px;
  background: rgba(34, 40, 49, 0.45);
}

.product-thumb {
  object-fit: contain;
  background: #fff;
  border-radius: 6px;
}

.empty-state {
  margin-top: 16px;
  padding: 24px;
  border: 1px dashed rgba(238, 238, 238, 0.25);
  border-radius: 12px;
  text-align: center;
  color: #aaa;
}
</style>
